import { useMutation, useQuery } from '@tanstack/react-query';
import { parseISO } from 'date-fns';
import { ReactNode, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Notification } from 'rsuite';
import { queryClient } from '../../../../../App';
import { FileUploadInvalidPayload, useModal } from '../../../../../components';
import { AlertWithTimestamp } from '../../../../../components/AlertWithTimestamp';
import { Exception } from '../../../../../data';
import { useNegotiationStatusStore } from '../../../../../store/useNegotiationStatusStore';
import { useSuppliersExhibitionStore } from '../../../../../store/useSuppliersExhibitionStore';
import { extractApiErrorMessage } from '../../../../../utils/extractApiErrorMessage';
import History from '../../../../../utils/History';
import { SalvarNegociacaoSchemaProps } from '../../components';
import { useNegotiationTable } from '../../hooks';
import { CHAVE_DO_MODAL_DA_MASSA } from '../NegociacaoFornecedor/verbaEmMassa';
import { POSTBulkChangeStatus, enviarVerbaEmMassaDaNegociacao } from './DetalhesDaNegociacao.bulk.services';
import {
  DELETENegotiation,
  DELETENegotiationDatapoint,
  EditarNegociacaoProps,
  GETDatapointsDetalhesDaNegociacao,
  GETNegotiationDetails,
  NegotiationUploadStartResponse,
  PATCHCloseNegotiation,
  PATCHReopenNegotiation,
  POSTDownloadNegotiation,
  POSTUploadNegotiationProductsFile,
  editarNegociacao,
} from './DetalhesDaNegociacao.services';
import { GetNegociacoesItemProps } from '../NegociacoesSalvas/NegociacoesSalvas.services';
import { visaoDaNegociacaoSalva } from './visaoDaNegociacaoSalva';
import type { EscolhasDaVerbaNoArquivo } from '../NegociacaoFornecedor/verbaNoArquivo';
import type { AdicionarVerbaEmMassaModalProps } from './components';

const ROW_KEY = 'productsToBePricedId';

type NegotiationDetailsWithUploadState = {
  status?: 'ONGOING' | 'CLOSED';
  hasProcessingFile?: boolean;
  hasFileProcessing?: boolean;
  isFileProcessing?: boolean;
  processingFile?: boolean;
  uploadProcessing?: boolean;
  statusFileProcessing?: string | null;
  uploadStatus?: string | null;
};

type FeedbackModalState = {
  type: 'success' | 'error';
  title: string;
  description: ReactNode;
  buttonTitle: string;
  onAction?: () => void;
};

const defaultProcessingDescription = (
  <>
    Seu arquivo está sendo validado e pode levar até 30 minutos.
    <br />
    Avisaremos por e-mail quando a negociação estiver pronta.
  </>
);

const getIsNegotiationUploadProcessing = (negotiationData?: NegotiationDetailsWithUploadState | null) => {
  if (!negotiationData) return false;

  const processingFlags = [
    negotiationData.hasProcessingFile,
    negotiationData.hasFileProcessing,
    negotiationData.isFileProcessing,
    negotiationData.processingFile,
    negotiationData.uploadProcessing,
  ];

  if (processingFlags.some(Boolean)) return true;

  const statusValues = [negotiationData.statusFileProcessing, negotiationData.uploadStatus].filter(Boolean).map(value => String(value).toUpperCase());

  return statusValues.some(value => ['PROCESSING', 'PENDING', 'IN_PROGRESS'].includes(value));
};

export const useDetalhesDaNegociacao = () => {
  const params = useParams() as Record<'id', string>;

  if (!params.id) History.push('/ipa/negociacoes/salvas');

  const handleNavBack = () => History.goBack();

  const [modals, { onHide: handleCloseModal, onOpen: handleOpenModal }] = useModal();

  const setNegotiationStatus = useNegotiationStatusStore(state => state.setNegotiationStatus);
  const clearNegotiationStatus = useNegotiationStatusStore(state => state.clearNegotiationStatus);
  const updateExhibitionType = useSuppliersExhibitionStore(state => state.updateExhibitionType);

  const { data: negotiationData, refetch: refetchNegotiationDetails } = useQuery<GetNegociacoesItemProps>({
    queryKey: ['negotiation/get-details'],
    queryFn: () => GETNegotiationDetails(params.id ?? ''),
    initialData: { id: '' },
  });

  useEffect(() => {
    if (negotiationData?.status) {
      setNegotiationStatus(negotiationData.status);
    }

    return () => {
      clearNegotiationStatus();
    };
  }, [negotiationData?.status, setNegotiationStatus, clearNegotiationStatus]);

  // A grade desta tela lê o agrupamento do ESTADO GLOBAL, e não recebe o grão da negociação como
  // propriedade.
  useEffect(() => {
    if (negotiationData?.visionSaved) {
      updateExhibitionType(visaoDaNegociacaoSalva(negotiationData.visionSaved), 'PRODUCT');
    }
  }, [negotiationData?.visionSaved, updateExhibitionType]);

  const [feedbackModalState, setFeedbackModalState] = useState<FeedbackModalState>({
    type: 'success',
    title: '',
    description: '',
    buttonTitle: 'FECHAR',
  });

  const openFeedbackModal = (state: FeedbackModalState) => {
    setFeedbackModalState(state);
    handleOpenModal('feedbackUploadArquivo');
  };

  const closeFeedbackModal = () => {
    setFeedbackModalState(oldState => ({
      ...oldState,
      onAction: undefined,
    }));
    handleCloseModal('feedbackUploadArquivo');
  };

  const refreshNegotiationAndCheckProcessing = async () => {
    const response = await refetchNegotiationDetails();
    return getIsNegotiationUploadProcessing(response.data as NegotiationDetailsWithUploadState);
  };

  const handleFeedbackPrimaryAction = () => {
    feedbackModalState.onAction?.();
    closeFeedbackModal();
  };

  const table = useNegotiationTable({
    queryFn: data => {
      return GETDatapointsDetalhesDaNegociacao({
        ...data,
        ipaNegotiationId: params.id,
      });
    },
    rowKey: ROW_KEY,
    negotiationStatus: negotiationData.status,
    negotiationId: params.id,
    // Enquanto o grão não chega, nenhum caminho específico dele é escolhido -- o padrão continua
    // sendo o de loja, que é o que a tela fazia antes de a rota declarar o grão.
    visionSaved: negotiationData?.visionSaved ? visaoDaNegociacaoSalva(negotiationData.visionSaved) : undefined,
  });

  const [isBulkActionLoading, setIsBulkActionLoading] = useState(false);
  const [isDownloadLoading, setIsDownloadLoading] = useState(false);

  /**
   * O grão gravado da negociação, ou nada enquanto a leitura não chega.
   *
   * Nada aqui é o que mantém a verba em massa fora do ar até o grão ser conhecido: a rota escreve
   * por uma estratégia diferente em cada grão, e escolher um por omissão gravaria em lote no
   * recorte errado.
   */
  const graoDaNegociacao = negotiationData?.visionSaved ? visaoDaNegociacaoSalva(negotiationData.visionSaved) : undefined;

  const verbaEmMassa: AdicionarVerbaEmMassaModalProps | null = graoDaNegociacao
    ? {
        show: modals[CHAVE_DO_MODAL_DA_MASSA],
        quantidadeSelecionada: table.selectedDatapoints.currentState.length,
        // Aqui a seleção JÁ É uma lista de identificadores de item. O modal decide o modo e o valor;
        // o corpo que os leva é montado ao lado do serviço desta tela, onde tem prova própria.
        enviar: enviarVerbaEmMassaDaNegociacao(table.selectedDatapoints.currentState, graoDaNegociacao),
        onHide: () => handleCloseModal(CHAVE_DO_MODAL_DA_MASSA),
        // A célula E a linha das aplicadas passam pela mesma porta que a gravação de uma linha
        // usa. A grade NÃO é recarregada: a resposta trouxe, item a item, o valor gravado e os
        // números que o motor derivou dele.
        onAplicado: resultado => table.adotarVerbaEmMassa(resultado),
      }
    : null;

  const handleBulkApprove = async () => {
    try {
      setIsBulkActionLoading(true);

      const payload = {
        negotiationSupplierDatapointStatus: 'APPROVED' as const,
        datapointsIds: table.selectedDatapoints.currentState,
        visionSaved: visaoDaNegociacaoSalva(negotiationData.visionSaved),
      };

      await POSTBulkChangeStatus(payload);

      await queryClient.refetchQueries(['get-negotiation-datapoints']);

      table.selectedDatapoints.reset();

      Notification.success({
        description: 'Itens aprovados com sucesso!',
      });
    } catch (err) {
      // O motor recusa nomeando o motivo; o texto fixo escondia a explicação dele.
      const errorMessage = extractApiErrorMessage(err);
      if (errorMessage) {
        AlertWithTimestamp({ message: errorMessage });
        return;
      }

      const { message } = new Exception('Erro ao aprovar itens em massa!');
      AlertWithTimestamp({ message });
    } finally {
      setIsBulkActionLoading(false);
    }
  };

  const handleBulkReject = async () => {
    try {
      setIsBulkActionLoading(true);

      const payload = {
        negotiationSupplierDatapointStatus: 'DECLINED' as const,
        datapointsIds: table.selectedDatapoints.currentState,
        visionSaved: visaoDaNegociacaoSalva(negotiationData.visionSaved),
      };

      await POSTBulkChangeStatus(payload);

      await queryClient.refetchQueries(['get-negotiation-datapoints']);

      table.selectedDatapoints.reset();

      Notification.success({
        description: 'Itens reprovados com sucesso!',
      });
    } catch (err) {
      // O motor recusa nomeando o motivo; o texto fixo escondia a explicação dele.
      const errorMessage = extractApiErrorMessage(err);
      if (errorMessage) {
        AlertWithTimestamp({ message: errorMessage });
        return;
      }

      const { message } = new Exception('Erro ao reprovar itens em massa!');
      AlertWithTimestamp({ message });
    } finally {
      setIsBulkActionLoading(false);
    }
  };

  const { mutate: handleCloseNegotiation } = useMutation({
    mutationFn: async (formData: Record<'id' | 'name', string>) => {
      const hasProcessingFile = await refreshNegotiationAndCheckProcessing();

      if (hasProcessingFile) {
        openFeedbackModal({
          type: 'error',
          title: 'Não é possível fechar a negociação',
          description: 'Existe um arquivo em processamento nesta negociação. Aguarde a conclusão para fechar o negócio.',
          buttonTitle: 'FECHAR',
        });
        throw new Error('NEGOTIATION_UPLOAD_PROCESSING');
      }

      const res = await PATCHCloseNegotiation(formData.id);
      return res.data;
    },
    onSuccess: async data => {
      queryClient.setQueryData(['negotiation/get-details'], {
        ...negotiationData,
        status: data.status,
      });
      setNegotiationStatus(data.status);

      await queryClient.refetchQueries(['get-negotiation-datapoints']);

      handleCloseModal('fecharNegocio');
      Notification.success({
        description: 'Negócio fechado.',
      });
    },
    onError: error => {
      if (error instanceof Error && error.message === 'NEGOTIATION_UPLOAD_PROCESSING') return;

      const errorMessage = extractApiErrorMessage(error);
      if (errorMessage) {
        AlertWithTimestamp({ message: errorMessage });
        return;
      }

      AlertWithTimestamp({ message: 'Erro ao fechar negócio' });
    },
  });

  const { mutate: handleEditNegotiation } = useMutation({
    mutationFn: async (formData: SalvarNegociacaoSchemaProps) => {
      const model: EditarNegociacaoProps = {
        negotiationId: params.id,
        name: formData.name,
        supplier: formData.supplier ?? '',
        startDate: formData.startDate,
        endDate: formData.endDate,
      } as const;
      const res = await editarNegociacao(model);
      return res.data;
    },
    onSuccess: data => {
      handleCloseModal('editarNegociacao');
      Notification.success({
        description: 'Negociação salva!',
      });
      queryClient.setQueriesData(['negotiation/get-details'], data);
    },
    onError: (error: unknown) => {
      const errorMessage = extractApiErrorMessage(error);
      if (errorMessage) {
        AlertWithTimestamp({ message: errorMessage });
        return;
      }
      AlertWithTimestamp({ message: 'Erro ao editar negociação' });
    },
  });

  const handleDeleteNegotiation = async () => {
    try {
      const response = await DELETENegotiation(params.id ?? '');
      if (response.status !== 204) return;

      handleCloseModal('excluirNegociacao');

      Notification.success({
        description: 'Negociação excluída!',
      });

      History.push('/ipa/negociacoes/salvas');
    } catch (err) {
      throw new Error('Erro durante a requisição!');
    }
  };

  const handleReopenNegotiation = async (formData: Record<'id' | 'name', string>) => {
    try {
      const response = await PATCHReopenNegotiation(formData.id);
      if (response.status !== 200) return;

      const newNegotiationId = response.data.id;

      setNegotiationStatus('ONGOING');

      await queryClient.refetchQueries(['get-negotiation-datapoints']);

      History.replace(`/ipa/negociacoes/salvas/${newNegotiationId}`);

      handleCloseModal('reabrirNegocio');
      Notification.success({
        description: 'Negócio reaberto.',
      });

      window.location.reload();
    } catch (err) {
      throw new Error('Erro durante a requisição');
    }
  };

  const handleDownload = async () => {
    try {
      setIsDownloadLoading(true);
      return await POSTDownloadNegotiation(params.id);
    } finally {
      setIsDownloadLoading(false);
    }
  };

  const handleOpenTableHeadingModal = async (modalName: string) => {
    if (modalName === 'fecharNegocio') {
      const hasProcessingFile = await refreshNegotiationAndCheckProcessing();

      if (hasProcessingFile) {
        openFeedbackModal({
          type: 'error',
          title: 'Não é possível fechar a negociação',
          description: 'Existe um arquivo em processamento nesta negociação. Aguarde a conclusão para fechar o negócio.',
          buttonTitle: 'FECHAR',
        });
        return;
      }
    }

    handleOpenModal(modalName);
  };

  const handleOpenUploadModal = async () => {
    const hasProcessingFile = await refreshNegotiationAndCheckProcessing();

    if (hasProcessingFile) {
      openFeedbackModal({
        type: 'error',
        title: 'Não é possível enviar um novo arquivo',
        description: 'Já existe um arquivo em processamento nesta negociação. Aguarde a finalização para enviar um novo arquivo.',
        buttonTitle: 'FECHAR',
      });
      return;
    }

    handleOpenModal('adicionarProdutosArquivo');
  };

  const handleUploadInvalidFile = (_payload: FileUploadInvalidPayload) => {
    handleCloseModal('adicionarProdutosArquivo');
    openFeedbackModal({
      type: 'error',
      title: 'Não foi possível processar o arquivo',
      description: 'Envie um arquivo em um dos formatos aceitos: .csv, .xls ou .xlsx.',
      buttonTitle: 'FECHAR',
      onAction: () => handleOpenModal('adicionarProdutosArquivo'),
    });
  };

  const handleUploadProductsFile = async (file: File, escolhasDaVerba: EscolhasDaVerbaNoArquivo | null = null) => {
    handleCloseModal('adicionarProdutosArquivo');

    try {
      const response = await POSTUploadNegotiationProductsFile(params.id, file, escolhasDaVerba);

      if (response.status === 202) {
        const responseData = response.data as NegotiationUploadStartResponse;

        openFeedbackModal({
          type: 'success',
          title: responseData?.title || 'Estamos processando o arquivo',
          description: responseData?.description || defaultProcessingDescription,
          buttonTitle: 'FECHAR',
        });

        await refetchNegotiationDetails();
      }
    } catch (error) {
      const defaultMessage =
        'O arquivo pode estar com formato inválido, colunas incorretas ou exceder o limite de 1.000 linhas. ' +
        'Envie um CSV ou XLSX com as colunas Product ID e Preço Unitário.';
      const errorMessage = extractApiErrorMessage(error);

      openFeedbackModal({
        type: 'error',
        title: 'Não foi possível processar o arquivo',
        description: errorMessage || defaultMessage,
        buttonTitle: 'TENTAR NOVAMENTE',
        onAction: () => handleOpenModal('adicionarProdutosArquivo'),
      });
    }
  };

  const handleDeleteDatapoint = async () => {
    try {
      const res = await DELETENegotiationDatapoint({
        negotiationId: params.id,
        datapoints: {
          excludedIds: table.selectedDatapoints.data.excluded,
          selectedIds: table.selectedDatapoints.data.selected,
        },
      });
      if (res.status !== 204) return null;
      queryClient.refetchQueries(['get-negotiation-datapoints']).then(res => {
        table.selectedDatapoints.reset();
        Notification.success({
          description: 'Produtos removidos!',
        });
        return res;
      });
      return res;
    } catch (err) {
      const { message } = new Exception('Erro genérico!');
      AlertWithTimestamp({ message });
    }
  };

  return {
    data: negotiationData,
    handleNavBack,
    table: {
      ...table,
      disableCheckBoxes: negotiationData.status === 'CLOSED',
      disableInputs: negotiationData.status === 'CLOSED',
    },
    heading: {
      name: negotiationData.name,
      status: negotiationData?.status,
      supplierId: negotiationData?.supplierId,
      supplierName: negotiationData?.supplierName,
      startDate: negotiationData?.startDate,
      endDate: negotiationData?.endDate,
      openModal: handleOpenModal,
    },
    tableHeading: {
      isSelected: !table.selectedDatapoints.isEmpty,
      openModal: handleOpenTableHeadingModal,
      hasSelectedDatapoints: !table.selectedDatapoints.isEmpty,
      selectedDatapointsLength: table.selectedDatapoints.currentState.length,
      totalElements: table.totalElements,
      status: negotiationData.status,
      onDownload: handleDownload,
      isDownloadLoading,
      onUpload: handleOpenUploadModal,
      onDeleteDatapoints: handleDeleteDatapoint,
      onBulkApprove: handleBulkApprove,
      onBulkReject: handleBulkReject,
      onAdicionarVerba: verbaEmMassa ? () => handleOpenModal(CHAVE_DO_MODAL_DA_MASSA) : undefined,
      isBulkActionLoading,
      showUploadButton: negotiationData.status === 'ONGOING',
      uploadTooltip: 'Adicionar novos produtos por arquivo',
    },
    modals: {
      adicionarVerbaEmMassa: verbaEmMassa,
      resumoDeEnvio: {
        show: modals.resumoDeEnvio,
        onHide: () => handleCloseModal('resumoDeEnvio'),
        negotiationId: params.id as string,
        // O modo de custo vem da grade, que ja o resolveu para desenhar as proprias celulas: uma
        // segunda consulta a configuracao do cliente poderia dizer "entrada" no seletor de
        // colunas e "liquido" na grade, na mesma tela.
        costNegotiationMode: table.costNegotiationMode,
      },
      editarNegociacao: {
        show: modals.editarNegociacao,
        handleOpen: () => handleOpenModal('editarNegociacao'),
        onHide: () => handleCloseModal('editarNegociacao'),
        onSubmit: handleEditNegotiation,
        values: {
          name: negotiationData.name ?? '--',
          supplier: negotiationData.supplierName ?? '--',
          startDate: parseISO(negotiationData.startDate as string),
          endDate: negotiationData.endDate ? parseISO(negotiationData.endDate) : undefined,
          enableFinalDate: !!negotiationData.endDate,
        },
      },
      excluirNegociacao: {
        show: modals.excluirNegociacao,
        name: negotiationData.name ?? '',
        onHide: () => handleCloseModal('excluirNegociacao'),
        onOpen: () => handleOpenModal('excluirNegociacao'),
        onSubmit: handleDeleteNegotiation,
      },
      fecharNegocio: {
        show: modals.fecharNegocio,
        data: { id: negotiationData.id, name: negotiationData.name },
        onHide: () => handleCloseModal('fecharNegocio'),
        onOpen: () => handleOpenModal('fecharNegocio'),
        onSubmit: handleCloseNegotiation,
      },
      reabrirNegocio: {
        show: modals.reabrirNegocio,
        data: { id: negotiationData.id, name: negotiationData.name },
        onHide: () => handleCloseModal('reabrirNegocio'),
        onOpen: () => handleOpenModal('reabrirNegocio'),
        onSubmit: handleReopenNegotiation,
      },
      adicionarProdutosArquivo: {
        show: modals.adicionarProdutosArquivo,
        onHide: () => handleCloseModal('adicionarProdutosArquivo'),
        onSubmit: handleUploadProductsFile,
        onInvalidFile: handleUploadInvalidFile,
      },
      feedbackUploadArquivo: {
        open: Boolean(modals.feedbackUploadArquivo),
        type: feedbackModalState.type,
        title: feedbackModalState.title,
        description: feedbackModalState.description,
        onClose: closeFeedbackModal,
        primaryAction: {
          label: feedbackModalState.buttonTitle,
          onClick: handleFeedbackPrimaryAction,
        },
      },
    },
  } as const;
};
